import { Droplets, CheckCircle2 } from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
} from "../../components/ui";
import { useSeo } from "../../lib/seo";

const RULES = [
  "AAMI water chemical analysis cadence (V201/V202)",
  "AAMI microbiological culture monthly (V252)",
  "AAMI water limit exceeded — CFU/EU (V178/V180)",
  "Bicarbonate Rx on file (V222-V234)",
  "IV iron protocol on file (V547/V549)",
  "Anemia program on file (V547)",
  "Modality change med rec on file (V582/V583)",
  "MBD labs in range",
  "ESA Hgb target range",
  "Vitamin D analog dose alignment",
  "Cinacalcet / etelcalcetide pairing",
  "Vascular access infection prophylaxis",
  "Hemodialyzer reuse oversight (§494.50)",
];

export function DialysisSolution() {
  useSeo({
    title: "Dialysis (ESRD) consultant pharmacy — WeConsult Rx",
    description:
      "CMS ESRD CfC rule pack: AAMI water quality, MBD labs, ESA Hgb, bicarbonate, IV iron, modality change med rec, hemodialyzer reuse. Pharmacy oversight finally codified.",
    path: "/solutions/dialysis",
  });

  return (
    <>
      <Section className="pb-8">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <Eyebrow>Dialysis · ESRD</Eyebrow>
              <Heading level={1} className="mt-3">
                CMS ESRD CfC. AAMI water. Pharmacy oversight, codified.
              </Heading>
              <Lede className="mt-6">
                First-mover coverage of dialysis pharmacy oversight under 42 CFR
                Part 494. AAMI water quality, bicarbonate Rx tracking, anemia
                management, and modality change medication reconciliation.
              </Lede>
              <div className="mt-8 flex gap-3">
                <LinkButton to="/demo">Book a dialysis demo</LinkButton>
                <LinkButton to="/features" variant="secondary">
                  See all features
                </LinkButton>
              </div>
            </div>
            <Card className="bg-forest-gradient text-white border-transparent">
              <Droplets className="h-10 w-10 mb-4" aria-hidden />
              <p className="text-sm uppercase tracking-tagline font-semibold text-white/85">
                42 CFR 494 + AAMI
              </p>
              <p className="mt-3 text-2xl font-bold">
                103+ dialysis checks across V-tag scope
              </p>
              <p className="mt-4 text-white/85 text-sm">
                Greenfield first-mover on AAMI water quality + pharmacy
                oversight. Each rule cites the V-tag verbatim.
              </p>
            </Card>
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>Rule coverage</Eyebrow>
            <Heading level={2} className="mt-2">
              The most comprehensive dialysis pharmacy rule pack on the market.
            </Heading>
          </div>
          <ul className="mt-10 grid gap-4 md:grid-cols-2">
            {RULES.map((r) => (
              <li
                key={r}
                className="flex items-start gap-3 rounded-xl border border-border bg-surface p-4"
              >
                <CheckCircle2 className="h-5 w-5 text-forest-700 mt-0.5" aria-hidden />
                <span className="text-sm text-ink-800 font-medium">{r}</span>
              </li>
            ))}
          </ul>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid gap-10 md:grid-cols-3">
            <div>
              <Heading level={3}>AAMI water quality</Heading>
              <p className="mt-3 text-ink-600">
                Chemical analysis cadence (annual), microbiological monthly,
                limits enforcement (200 CFU/mL · 2 EU/mL).
              </p>
            </div>
            <div>
              <Heading level={3}>Pharmacy oversight</Heading>
              <p className="mt-3 text-ink-600">
                Bicarbonate Rx, IV iron protocol, anemia program, modality
                change med rec — each anchored to a V-tag.
              </p>
            </div>
            <div>
              <Heading level={3}>Water-quality + pharmacy oversight</Heading>
              <p className="mt-3 text-ink-600">
                We ship AAMI water-quality and pharmacy-oversight rules many
                legacy vendors classify as biomed scope.
              </p>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
